import { useState } from "react";

import { briefingApiSlice } from "api/briefingApi.slice";
import { Checkbox, UIButton } from "common/components";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTrack } from "common/utilities/useTrack";
import { useTranslate } from "common/utilities/useTranslate";
import { briefingGenerationSlice } from "domains/briefing/briefingGeneration.slice";
import { BriefingListItem } from "domains/briefing/components/BriefingListItem/BriefingListItem";
import * as StyledItem from "domains/briefing/components/BriefingListItem/BriefingListItem.styled";
import * as Styled from "domains/briefing/components/BriefingModal/BriefingModal.styled";
import { getBriefings } from "domains/briefing/selectors";
import { ModalHeader } from "domains/modal/components/ModalHeader";
import { getModalParams } from "domains/modal/selectors";
import type { ModalParams } from "domains/modal/types";

export const BriefingModal = () => {
  const dispatch = useDispatch();
  const translate = useTranslate();
  const briefings = useSelector(getBriefings);
  const { topicId, topicName } = useSelector(getModalParams) as ModalParams<"Briefing">;
  const [includeForesight, setIncludeForesight] = useState<boolean>(true);
  const [includeFurtherReadings, setIncludeFurtherReadings] = useState<boolean>(true);
  const track = useTrack();

  const { addBriefing, updateBriefingJobStatus } = briefingGenerationSlice.actions;

  const [triggerBriefingGeneration, { data: briefingGenerationJobData, isLoading, isError }] =
    briefingApiSlice.endpoints.triggerBriefingGeneration.useMutation();

  const handleIncludeFurtherReadings = () => {
    setIncludeFurtherReadings(!includeFurtherReadings);
  };

  const handleIncludeForesight = () => {
    setIncludeForesight(!includeForesight);
  };

  const generateBriefing = async () => {
    track({
      name: "Generate briefing",
      entityId: topicId,
      entityName: topicName,
      entityType: "Topic",
      secondaryEntityType: "Briefing - Advanced",
    });
    const { data } = await triggerBriefingGeneration({
      topicId,
      isFurtherReadingEnabled: includeFurtherReadings,
      isForesightEnabled: includeForesight,
    });

    if (data) {
      dispatch(
        addBriefing({
          id: data?.id ?? "",
          status: "Processing",
          progress: 0,
          downloadUrl: "",
          topicId,
        }),
      );
      return;
    }

    dispatch(
      updateBriefingJobStatus({
        id: briefingGenerationJobData?.id ?? "",
        status: "Error",
      }),
    );
  };

  const title = translate("app.briefing.title");

  return (
    <>
      <ModalHeader title={title} />
      <Styled.ExplanationParagraph>{translate("app.briefing.advanced_briefing_note")}</Styled.ExplanationParagraph>
      <div>
        <Styled.GenerationContent>
          <Styled.Text>{translate("app.briefing.configuration_note")}</Styled.Text>
          <Styled.OptionCheckbox
            id="further_readings"
            text={translate("app.briefing.further_reading_checkbox")}
            value={includeFurtherReadings}
            onChange={handleIncludeFurtherReadings}
          />
          <Checkbox
            id="foresight"
            text={translate("app.briefing.foresight_checkbox")}
            value={includeForesight}
            onChange={handleIncludeForesight}
          />
        </Styled.GenerationContent>
        <UIButton variant="filled" onClick={generateBriefing} isLoading={isLoading} style={{ width: "100%" }}>
          {translate("app.briefing.generate_button")}
        </UIButton>
        <Styled.BriefingList hasListItems={briefings.length > 0 || isError}>
          {briefings.map((briefing) => (
            <BriefingListItem
              key={briefing.id}
              topicId={briefing.topicId}
              progress={briefing.progress}
              status={briefing.status}
              briefingId={briefing.id}
            />
          ))}
          {isError && (
            <StyledItem.ItemBox status="Error">
              <StyledItem.BriefingTitle>{topicName}</StyledItem.BriefingTitle>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <StyledItem.Text>{translate("app.briefing.error")}</StyledItem.Text>
              </div>
            </StyledItem.ItemBox>
          )}
        </Styled.BriefingList>
      </div>
    </>
  );
};
